/**
 * AB#820: "you have changes the live site has not seen yet".
 *
 * Sits next to Preview site in the site sidebar: a count pill that opens the
 * list of saved-but-unpublished changes and offers the same publish the
 * Generation page runs.
 */
import { useQueryClient } from '@tanstack/react-query';
import { siteKeys, useDeployState, useDeployToCloudflare, type DeployPendingChange } from '@wordpuppi/shared';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Files, Loader2, Newspaper, Palette, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';

const KINDS = {
  post: { icon: Newspaper, label: 'Post' },
  page: { icon: Files, label: 'Page' },
  settings: { icon: SlidersHorizontal, label: 'Settings' },
  theme: { icon: Palette, label: 'Theme' },
} as const;

// ponytail: absolute local time, not "3 minutes ago" — the rest of the admin
// already reads timestamps with toLocaleString and no relative-time helper exists.
const when = (iso: string) => new Date(iso).toLocaleString();

function PendingRow({ item }: { item: DeployPendingChange }) {
  const kind = KINDS[item.kind] ?? KINDS.settings;
  return (
    <li data-testid="deploy-pending-item" className="flex items-start gap-2">
      <kind.icon aria-hidden="true" className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0">
        <span className="block truncate font-medium">{item.label}</span>
        <span className="block text-xs text-muted-foreground">{kind.label} · {when(item.changed_at)}</span>
      </span>
    </li>
  );
}

export function DeployBadge({ siteSlug }: { siteSlug: string }) {
  const { data } = useDeployState(siteSlug);
  const queryClient = useQueryClient();
  const deploy = useDeployToCloudflare();
  const state = data?.data;

  // Nothing pending (or never published) means nothing to tell anyone about.
  if (!state || state.count === 0) return null;

  const handleDeploy = async () => {
    try {
      const result = await deploy.mutateAsync({ site_slug: siteSlug });
      toast.success(`Deployed! ${result.url}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Deployment failed', { duration: 20000 });
    } finally {
      // The publish stamp moved (or the failure left it alone) — either way the
      // pending list on screen is now a guess.
      void queryClient.invalidateQueries({ queryKey: siteKeys.deployState(siteSlug) });
    }
  };

  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            data-testid="deploy-badge"
            aria-label={`${state.count} ${state.count === 1 ? 'change' : 'changes'} not deployed`}
            title="Changes not deployed"
            className="inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground outline-none transition-[background-color,opacity,scale] duration-(--duration-popover) ease-out starting:scale-95 starting:opacity-0 motion-reduce:starting:scale-100 hover:bg-primary/90 focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            {state.count}
          </button>
        }
      />
      <PopoverContent align="end" data-testid="deploy-badge-popover" className="w-72 max-w-[calc(100vw-1rem)] max-h-[var(--available-height)] space-y-3 overflow-y-auto p-3 text-sm">
        <p className="font-medium">Not deployed yet</p>
        <p className="text-xs text-muted-foreground">
          {state.last_deployed_at ? `Last deployed ${when(state.last_deployed_at)}` : 'Never deployed'}
        </p>
        <ul className="space-y-2">
          {state.pending.map((item) => (
            <PendingRow key={`${item.kind}:${item.id ?? item.label}`} item={item} />
          ))}
        </ul>
        {state.count > state.pending.length && (
          <p className="text-xs text-muted-foreground">and {state.count - state.pending.length} more</p>
        )}
        {state.can_deploy ? (
          <Button
            type="button"
            size="sm"
            className="w-full"
            data-testid="deploy-badge-deploy"
            disabled={deploy.isPending}
            aria-busy={deploy.isPending}
            onClick={() => void handleDeploy()}
          >
            {deploy.isPending && <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />}
            {deploy.isPending ? 'Deploying…' : 'Deploy'}
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">Connect a Cloudflare Pages project in Settings → Generation to publish.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}
